import posthog from 'posthog-js'
import { Capacitor } from '@capacitor/core'

export function usePostHog() {
  const getPlatformInfo = () => {
    if (Capacitor.isNativePlatform()) {
      if (Capacitor.getPlatform() === 'ios') {
        return 'ios'
      } else if (Capacitor.getPlatform() === 'android') {
        return 'android'
      }
      return 'mobile'
    }
    return 'web'
  }

  // Check if PostHog should be enabled (not in tests and in production)
  const isPostHogEnabled =
    import.meta.env.PROD &&
    !import.meta.env.VITE_DISABLE_POSTHOG &&
    typeof window !== 'undefined' &&
    !window.navigator?.userAgent?.includes('Playwright')

  if (isPostHogEnabled) {
    posthog.init(import.meta.env.VITE_POSTHOG_API_KEY, {
      api_host: import.meta.env.VITE_POSTHOG_HOST,
      // Disable session replay in development
      session_recording: import.meta.env.DEV
        ? undefined
        : {
            recordCrossOriginIframes: false,
          },
      capture_pageview: true,
      capture_pageleave: true,
      person_profiles: 'identified_only',
      autocapture: true,
      capture_exceptions: {
        capture_unhandled_errors: true,
        capture_unhandled_rejections: true,
        capture_console_errors: true,
      },
      // bootstrap: {
      //   featureFlags: false,
      // } as Record<string, string | boolean>,
    })

    posthog.register({
      source: 'app',
      platform: getPlatformInfo(),
      app_version: import.meta.env.VITE_APP_VERSION,
    })
  }

  return {
    posthog,
    track: (eventName: string, properties?: Record<string, any>) => {
      if (isPostHogEnabled && typeof posthog.capture === 'function') {
        try {
          posthog.capture(eventName, properties)
        } catch (error) {
          // Silently fail in case PostHog isn't fully initialized
          console.warn('PostHog track failed:', error)
        }
      }
    },
    identify: (userId: string, userProperties?: Record<string, any>) => {
      if (isPostHogEnabled && typeof posthog.identify === 'function') {
        try {
          const propertiesWithPlatform = {
            ...userProperties,
            platform: getPlatformInfo(),
            app_version: import.meta.env.VITE_APP_VERSION,
          }
          posthog.identify(userId, propertiesWithPlatform)
        } catch (error) {
          // Silently fail in case PostHog isn't fully initialized
          console.warn('PostHog identify failed:', error)
        }
      }
    },
    setUserProperties: (properties: Record<string, any>) => {
      if (isPostHogEnabled && typeof posthog.people?.set === 'function') {
        try {
          const propertiesWithPlatform = {
            ...properties,
            platform: getPlatformInfo(),
            app_version: import.meta.env.VITE_APP_VERSION,
          }
          posthog.people.set(propertiesWithPlatform)
        } catch (error) {
          // Silently fail in case PostHog isn't fully initialized
          console.warn('PostHog setUserProperties failed:', error)
        }
      }
    },
    identifyExistingUser: (user: any) => {
      if (
        isPostHogEnabled &&
        typeof posthog.identify === 'function' &&
        user?.email
      ) {
        try {
          const userProperties = {
            cognitoId: user.cognitoId,
            email: user.email,
            firstName: user.firstName,
            lastName: user.lastName,
            platform: getPlatformInfo(),
            app_version: import.meta.env.VITE_APP_VERSION,
          }
          posthog.identify(user.email, userProperties)
        } catch (error) {
          // Silently fail in case PostHog isn't fully initialized
          console.warn('PostHog identifyExistingUser failed:', error)
        }
      }
    },
    isFeatureEnabled: (flagName: string): boolean => {
      if (isPostHogEnabled && typeof posthog.isFeatureEnabled === 'function') {
        try {
          return posthog.isFeatureEnabled(flagName) || false
        } catch (error) {
          console.warn('PostHog isFeatureEnabled failed:', error)
          return false
        }
      }
      return false
    },
  }
}
